// RuntimeWizardWithBackground.jsx
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Button, CircularProgress, Container, Typography, Box } from '@mui/material';
import wizardApi from '../../../../service';
import RuntimeWizard from '../RuntimeWizard';

const RuntimeWizardWithBackground = () => {
    const { wizardId } = useParams();
    const [wizardSchema, setWizardSchema] = useState(null);
    const [wizardTitle, setWizardTitle] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadWizard = async () => {
            try {
                setLoading(true);
                if (wizardId) {
                    const wizard = await wizardApi.getWizard(wizardId);
                    if (wizard?.jsonSchema) {
                        setWizardSchema(wizard.jsonSchema);
                        setWizardTitle(wizard.title || `Form ${wizardId}`);
                    } else {
                        throw new Error('Form not found or has no schema');
                    }
                } else {
                    throw new Error('No form ID provided');
                }
                setError(null);
            } catch (err) {
                console.error('Error loading form:', err);
                setError(err.message);
                setWizardSchema(null);
            } finally {
                setLoading(false);
            }
        };

        loadWizard();
    }, [wizardId]);

    // Background image style with fallback
    const backgroundStyle = {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundImage: `
            url('https://static.wixstatic.com/media/4d73a4_9f6bcb7638b24332a90498bb94a044e7~mv2.png/v1/fill/w_1905,h_927,al_c,q_90,usm_0.66_1.00_0.01,enc_avif,quality_auto/4d73a4_9f6bcb7638b24332a90498bb94a044e7~mv2.png'),
            linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)
        `,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        opacity: 0.1, // Slightly increased opacity
        zIndex: -1,
        filter: 'blur(0.5px)', // Optional: subtle blur effect
    };

    if (loading) {
        return (
            <>
                <div style={backgroundStyle} />
                <Container maxWidth="md" sx={{
                    py: 4,
                    textAlign: 'center',
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative',
                    zIndex: 1
                }}>
                    <CircularProgress size={60} thickness={4} />
                    <Typography variant="h6" sx={{ mt: 3, color: 'text.secondary' }}>
                        Loading form...
                    </Typography>
                </Container>
            </>
        );
    }

    if (error || !wizardSchema) {
        return (
            <>
                <div style={backgroundStyle} />
                <Container maxWidth="md" sx={{
                    py: 4,
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    position: 'relative',
                    zIndex: 1
                }}>
                    <Alert severity="error" sx={{ mb: 3, py: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Unable to Load Form
                        </Typography>
                        <Typography>
                            {error || 'Failed to load form. Please try again.'}
                        </Typography>
                    </Alert>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Button
                            variant="outlined"
                            onClick={() => window.history.back()}
                            size="large"
                        >
                            Go Back
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => window.location.href = '/forms'}
                            size="large"
                        >
                            Browse All Forms
                        </Button>
                        <Button
                            variant="text"
                            onClick={() => window.location.href = '/'}
                            size="large"
                        >
                            Go to Home
                        </Button>
                    </Box>
                </Container>
            </>
        );
    }

    return (
        <>
            <div style={backgroundStyle} />
            <Box sx={{
                minHeight: '100vh',
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                py: { xs: 2, sm: 4 },
                px: { xs: 1, sm: 2 }
            }}>
                {/* Form Container - WIDER (70% width) */}
                <Container 
                    maxWidth="lg" // Changed from "md" to "lg" for wider container
                    sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        borderRadius: 2,
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                        py: 4,
                        px: { xs: 3, sm: 4 },
                        my: 4,
                        position: 'relative',
                        overflow: 'visible',
                        width: '70%', // Force 70% width
                        maxWidth: '70% !important', // Override default maxWidth
                    }}
                >
                    {/* Form Title */}
                    {wizardTitle && (
                        <Box sx={{ mb: 4, textAlign: 'center' }}>
                            {/* <Typography
                                variant="h4"
                                component="h1"
                                sx={{
                                    fontWeight: 600,
                                    color: 'primary.main',
                                    mb: 1
                                }}
                            >
                                {wizardTitle}
                            </Typography> */}
                            <Typography
                                variant="body1"
                                color="text.secondary"
                                sx={{ maxWidth: 800, mx: 'auto' }} // Increased maxWidth
                            >
                                Please fill out the form below
                            </Typography>
                        </Box>
                    )}

                    {/* The Actual Form - Make RuntimeWizard take 100% of container width */}
                    <Box sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                    }}>
                        <Box sx={{
                            width: '100%', // RuntimeWizard takes full width of container
                            maxWidth: '100%',
                            '& > *': {
                                width: '100% !important',
                                maxWidth: '100% !important',
                            }
                        }}>
                            <RuntimeWizard wizardSchema={wizardSchema} />
                        </Box>
                    </Box>
                </Container>
            </Box>
        </>
    );
};

export default RuntimeWizardWithBackground;